import React, { useState, useEffect, useRef } from "react";
// REDUX
import { useSelector, useDispatch, useStore } from "react-redux";
import { updateFilters, updateBlockFilters } from "../../../redux/reports/reportsSlice";
import { usePostFaultChartMutation } from "../../../redux/reports/chartsAPI";
// COMPONENTS
import ReportsHeader from "../ReportsHeader";
import ChartBar from "../charts/ChartBar";
import ChartPie from "../charts/ChartPie";
import ChartLine from "../charts/ChartLine";
// AUTHORIZATION
import validatePermission from "../../../utils/validatePermissions";
import UnauthorizedPage from "../../unauthorized/UnauthorizedPage";
// INTERFACES
import { RootState } from "../../../app/store";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { getTotalCarsInBank, generateFaultDayWeek } from "../utils";

const ReportFaultChartDayEscMov = () => {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const store = useStore();

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [chartData, setChartData] = useState<any>([]);
	const skipFirstFiltersFetch = useRef(true);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { permissions } = useSelector((state: RootState) => state.user);
	const { theme } = useSelector((state: RootState) => state.home);
	const { filters, reportRefreshTable } = useSelector((state: RootState) => state.report);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [triggerGetChart, { data, isLoading, isError, error }] = usePostFaultChartMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const getChartData = async () => {
		const token = await GETJwtToken();
		triggerGetChart({
			token,
			body: {
				bank_id: filters.bankId,
				start_date: filters.startDate,
				end_date: filters.endDate,
				timezone: filters.timezoneCode,
				chart_by: "DAY",
				...(filters.liftType !== "ALL" && { lift_type: filters.liftType }),
			},
		});
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		if (data && data.results) {
			const response = generateFaultDayWeek(data.results);
			setChartData(response);
		} else {
			setChartData([{ value: 0, name: "", tag: "" }]);
		}
	}, [data]);

	useEffect(() => {
		if (skipFirstFiltersFetch.current) {
			skipFirstFiltersFetch.current = false;
			return;
		}

		if (
			filters.bankId !== "" &&
			filters.startDate !== "" &&
			filters.endDate !== "" &&
			filters.timezoneCode !== "" &&
			filters.liftType === "ESCALATOR" &&
			!isLoading
		) {
			getChartData();
		}
	}, [filters.bankId, filters.startDate, filters.endDate, filters.timezoneCode, filters.liftType]);

	useEffect(() => {
		if (chartData && chartData.length > 0) {
			const response = getTotalCarsInBank(chartData);
			// Get the current state from Redux store to avoid stale closure values
			const currentFilters = (store.getState() as RootState).report.filters;
			dispatch(updateFilters({
				...currentFilters,
				selectedCarsList: response,
				carsList: response,
			}));
		}
	}, [chartData, dispatch, store]);

	useEffect(() => {
		dispatch(updateBlockFilters(isLoading));
		return () => { dispatch(updateBlockFilters(false)); };
	}, [isLoading]);

	useEffect(() => {
		if (reportRefreshTable) {
			getChartData();
		}
	}, [reportRefreshTable]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			{validatePermission("view-report-management-faults-by-day-week", permissions) ? (
				<div className={`layout__contentPage__${theme}`}>
					<ReportsHeader
						reportDefaultData={{
							reportType: "CHART",
							levelTypeName: "BANKS",
							displaySelectTypeLevel: false,
							liftType: "ESCALATOR",
							defaultColumns: [],
							defaultOffsets: [],
							infoDescriptionText: "report.infoDescriptionText2",
							displayDownloadCSVReport: false,
							displayDownloadPDFReport: true,
							reportTitle: "Faults / Faults by Day - Week",
							displayAdvanceFiltersExclusions: false,
							displayAdvanceFiltersOffsets: false,
							disabledOffsets: true,
							useTablePagination: true,
							displayDateByRange: false,
							displayDate: true,
							displaySelectLiftType: false,
							displayAdvanceFilterCharts: true,
							displayAdvanceFiltersTable: false,
							displayRecentWeeksPicket: false,
							displayInputLimitItems: false,
							displayChartMirror: false,
							chartMirror: false,
							chartType: "bar_stack_vertical",
							dateTypeOptions: [
								{ id: "TODAY", name: "Day" },
								{ id: "WEEK", name: "Week" },
								{ id: "MONTH", name: "Month" },
								{ id: "QUARTER", name: "Quarter" },
								{ id: "YEAR", name: "Year" },
							],
							dateDaysLimit: 0,
							displayMinimumActivation: false,
							dateWeeksLimit: 0,
							dateMonthsLimit: 0,
							dateQuarterLimit: 0,
							dateYearsLimit: 0,
							dateType: "MONTH",
							bankSearchAndFilterString: "&filter_target=type,type&filter=ESCALATOR,MOVINGWALK",
						}}
					/>
					{(filters.chartType === "line" ||
						filters.chartType === "line_natural" ||
						filters.chartType === "step_before" ||
						filters.chartType === "step_after") && (
							<ChartLine data={chartData} isLoading={isLoading} isError={isError} errorMessage={error && (error as any).data?.message} bottomLabel='DAY' leftLabel='FAULTS' />
						)}
					{filters.chartType === "pie" && <ChartPie charData={chartData} />}
					{(filters.chartType === "bar_vertical" ||
						filters.chartType === "bar_stack_vertical" ||
						filters.chartType === "bar_horizontal" ||
						filters.chartType === "bar_stack_horizontal") && (
							<>
								<ChartBar data={chartData} isLoading={isLoading} isError={isError} errorMessage={error && (error as any).data?.message} bottomLabel='DAY' leftLabel='FAULTS' />
							</>
						)}
				</div>
			) : (
				<UnauthorizedPage />
			)}
		</div>
	);
};

export default ReportFaultChartDayEscMov;
